import secureRequest from "@toothfairy/shared-api/secureAPI";
import client from "@toothfairy/shared-api/src/Common/client";
import envConfig from "../../../envConfig";
import Common from "../Common";

/**
 * Isolated tool & hook testing API.
 *
 * Backs the ChatECS `POST /test/tool` and `POST /test/hook` endpoints, which
 * execute a SINGLE configured AgentFunction (REST / GraphQL / Database /
 * CodeTemplate) or a SINGLE Custom Execution Environment hook with explicit
 * arguments — no LLM tool-selection loop. Used by the Functions and Execution
 * Environments "Test" modals to validate configuration in isolation.
 *
 * IMPORTANT: these routes live on the ChatECS Flask app (the AIS host), NOT the
 * AI REST endpoint. The host is region-aware via envConfig.REACT_APP_AIS_ENDPOINT
 * (e.g. https://ais.toothfairyai.com / ais.eu / ais.us / ais.toothfairylab.link).
 *
 * Both endpoints resolve the workspace from the `x-api-key` token (same as
 * /predictions), so only the token + the target id + arguments are required.
 *
 * RESPONSE CONTRACT: every call resolves to a NORMALISED object so the UI has
 * 100% visibility regardless of transport vs. tool-execution failure::
 *
 *   {
 *     success:    bool,       // OVERALL success (transport OK AND tool OK)
 *     transport:  bool,       // true = HTTP request reached the server & returned
 *     httpStatus: int|null,   // raw HTTP status code (e.g. 200, 401, 500)
 *     problem:    string|null,// apisauce problem label (transport failure only)
 *     type:       string|null,// tool type reported by the server (api/graphql/db/code)
 *     toolSuccess:bool|null,  // tool-level success flag from the server payload
 *     result:     any,        // the raw result payload (or server error body)
 *     status_code:int|null,   // tool-level status (from server payload, if any)
 *     error:      string|null,// human-readable error message (always populated on failure)
 *   }
 *
 * This deliberately splits TRANSPORT success (did the request reach ChatECS and
 * get a response?) from TOOL-EXECUTION success (did the configured tool itself
 * run without error?) so the modal can surface the exact failure layer rather
 * than masking a 500 as a success.
 */

const _buildHeaders = (token) => {
  let headers;
  if (token) headers = { "x-api-key": token };
  return headers;
};

/**
 * Normalise an apisauce response into the contract above.
 *
 * apisauce returns { ok, status, problem, data, ... }. On a non-2xx response
 * `ok` is false and `data` holds the error body. On a network/timeout failure
 * `problem` is set (e.g. "TIMEOUT_ERROR", "CONNECTION_ERROR") and `status` may
 * be null. We fold all of that into one predictable shape.
 */
const _normalise = (response, defaultError) => {
  // Network / transport failure (no HTTP response at all)
  if (response && response.problem && response.ok === false && response.status == null) {
    return {
      success: false,
      transport: false,
      httpStatus: null,
      problem: response.problem,
      type: null,
      toolSuccess: null,
      result: response.data ?? null,
      status_code: null,
      error:
        response.data?.error ||
        response.data?.errorMessage ||
        response.problem ||
        defaultError,
    };
  }

  const httpStatus = response?.status ?? null;
  const data = response?.data;
  const transportOk = response?.ok === true;

  // Server returned a body — it may be our contract (success/type/result/...) or
  // an error envelope ({ error, errorCode, errorMessage }).
  const toolSuccess =
    data && typeof data === "object" ? data.success : undefined;
  const toolType = data && typeof data === "object" ? data.type : null;
  const toolResult = data && typeof data === "object" ? data.result : data;
  const toolStatusCode =
    data && typeof data === "object" ? data.status_code : null;

  // Overall success requires BOTH transport ok AND the server's tool success
  // flag being truthy. When the server payload has no `success` field we fall
  // back to transport ok (e.g. a 200 with a raw payload).
  const overall =
    transportOk && (toolSuccess === true || toolSuccess === undefined);

  const errorMsg =
    data && typeof data === "object"
      ? data.error || data.errorMessage || null
      : null;

  return {
    success: overall,
    transport: transportOk,
    httpStatus,
    problem: transportOk ? null : response?.problem || null,
    type: toolType ?? null,
    toolSuccess: toolSuccess ?? null,
    result: toolResult ?? null,
    status_code: toolStatusCode ?? null,
    error: overall ? null : errorMsg || `HTTP ${httpStatus || "?"} ${response?.problem || ""}`.trim(),
  };
};

/**
 * Execute a single AgentFunction by id with explicit arguments.
 */
const testTool = async ({ token, functionId, arguments: args = {}, chatData }) => {
  const url = `${envConfig.REACT_APP_AIS_ENDPOINT}/test/tool`;

  const body = Common.objectCleaner({
    functionId,
    arguments: args,
    chatData,
  });

  let response;
  try {
    response = await secureRequest(
      client,
      "POST",
      url,
      null,
      body,
      false,
      _buildHeaders(token),
    );
  } catch (e) {
    return {
      success: false,
      transport: false,
      httpStatus: null,
      problem: "CLIENT_EXCEPTION",
      type: null,
      toolSuccess: null,
      result: null,
      status_code: null,
      error: e?.message || "Request failed to send",
    };
  }
  return _normalise(response, "Tool test request failed");
};

/**
 * Execute a single Custom Execution Environment hook with explicit params.
 */
const testHook = async ({
  token,
  environmentId,
  arguments: args = {},
  chatId,
  messageId,
}) => {
  const url = `${envConfig.REACT_APP_AIS_ENDPOINT}/test/hook`;

  const body = Common.objectCleaner({
    environmentId,
    arguments: args,
    chatId,
    messageId,
  });

  let response;
  try {
    response = await secureRequest(
      client,
      "POST",
      url,
      null,
      body,
      false,
      _buildHeaders(token),
    );
  } catch (e) {
    return {
      success: false,
      transport: false,
      httpStatus: null,
      problem: "CLIENT_EXCEPTION",
      type: null,
      toolSuccess: null,
      result: null,
      status_code: null,
      error: e?.message || "Request failed to send",
    };
  }
  return _normalise(response, "Hook test request failed");
};

export default {
  testTool,
  testHook,
};
